import React, { useState } from "react";

type InputProps = {
  label?: string;
  showLabel?: boolean;
  description?: string;
  showDescription?: boolean;
  linkText?: string;
  showLink?: boolean;
  onLinkClick?: () => void;
  placeholder?: string;
  value?: string | number;
  name?: string;
  id?: string;
  type?: string;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  icon?: React.ReactNode;
  showIcon?: boolean;
  suffix?: React.ReactNode;
  showCount?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  className?: string;
  autoComplete?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

const Input: React.FC<InputProps> = ({
  label,
  showLabel = true,
  description,
  showDescription = false,
  linkText,
  showLink = false,
  onLinkClick,
  placeholder,
  value,
  name,
  id,
  type = "text",
  disabled = false,
  hasError = false,
  errorMessage,
  icon,
  showIcon = false,
  suffix,
  showCount = false,
  maxLength,
  min,
  max,
  className,
  autoComplete = "off",
  onChange,
  onBlur,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && isPasswordVisible ? "text" : type;

  return (
    <div className={`flex flex-col gap-1 ${className ?? ""}`}>
      {(showLabel || showLink) && (
        <div className="flex items-center justify-between">
          {showLabel && label && (
            <label
              htmlFor={id}
              className="text-body-emphasis text-[#303030]"
            >
              {label}
            </label>
          )}
          {showLink && linkText && (
            <button
              type="button"
              onClick={onLinkClick}
              className="text-body-sm-emphasis text-[#2B8565] hover:underline"
            >
              {linkText}
            </button>
          )}
        </div>
      )}

      <div className="relative flex items-center">
        {showIcon && icon && (
          <span className="absolute left-3 text-[#A1A1AA] pointer-events-none">
            {icon}
          </span>
        )}

        <input
          id={id}
          name={name}
          type={inputType}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          min={min}
          max={max}
          autoComplete={autoComplete}
          aria-invalid={hasError || undefined}
          aria-describedby={
            id
              ? [
                  hasError && errorMessage ? `${id}-error` : "",
                  showDescription && description ? `${id}-desc` : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              : undefined
          }
          onChange={onChange}
          onBlur={onBlur}
          className={`block w-full h-10 rounded-md border text-sm font-normal leading-5 text-[#303030] placeholder:text-body placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] transition-colors ${
            showIcon && icon ? "pl-9 pr-3" : "px-3"
          } ${
            isPassword ? "pr-10" : ""
          } ${
            disabled
              ? "opacity-50 cursor-not-allowed bg-[#E8E8E8] text-[#6B6B6B]"
              : "bg-white"
          } ${
            hasError
              ? "border-error ring-1 ring-error focus:border-error focus:ring-error"
              : "border-[#E8E8E8]"
          }`}
        />

        {isPassword && (
          <button
            type="button"
            aria-label={isPasswordVisible ? "Hide password" : "Show password"}
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            className="absolute right-3 text-[#6B6B6B] hover:text-[#303030]"
          >
            {isPasswordVisible ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M1 12C1 12 5 20 12 20C19 20 23 12 23 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20C5 20 1 12 1 12A18.45 18.45 0 0 1 5.06 5.06M9.9 4.24A9.12 9.12 0 0 1 12 4C19 4 23 12 23 12A18.5 18.5 0 0 1 19.18 17.94" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        )}

        {suffix && (
          <span className="ml-1 text-sm font-bold text-[#303030]">
            {suffix}
          </span>
        )}
      </div>

      {showCount && maxLength && (
        <span className="text-xs text-gray text-end">
          {String(value ?? "").length}/{maxLength}
        </span>
      )}

      {hasError && errorMessage && (
        <span id={id ? `${id}-error` : undefined} className="text-xs text-red">{errorMessage}</span>
      )}

      {showDescription && description && (
        <span id={id ? `${id}-desc` : undefined} className="text-xs text-gray">{description}</span>
      )}
    </div>
  );
};

export default Input;
